// ---------------------------------------------------------------------------
// backgroundRemoval.js — @imgly/background-removal (ONNX, client-side) wrapper.
//
// Foundation owns: the imgly call, quality selector, download-vs-inference
// progress, preload-on-open, and a size cap. The library is dynamically
// imported from the imgly CDN (no publicPath) so the heavy WASM/ONNX payload
// only loads when the editor actually needs it.
//
// SEAM for the later mask-refinement agent (Group G):
//   `segmentForegroundMask()` and `applyForegroundMask()` below expose the
//   1.7.0 mask API (segmentForeground / applySegmentationMask). The refine
//   brush should: (1) call segmentForegroundMask() to get an editable alpha
//   mask, (2) let the user restore/erase on that mask with a soft brush,
//   (3) call applyForegroundMask() with the edited mask for a before/after
//   accept-reject. Keep all of that in this module + a small panel; do not
//   scatter imgly calls into the editor class.
// ---------------------------------------------------------------------------

// Quality tiers. Bigger model = better edges, larger download.
export const BG_QUALITY = {
  fast:    { model: 'isnet_quint8', label: 'Rapid (~44MB)' },
  balanced:{ model: 'isnet_fp16',   label: 'Echilibrat (~88MB)' },
  best:    { model: 'isnet',        label: 'Maxim (~176MB)' },
};
export const DEFAULT_BG_QUALITY = 'balanced';

// Guard against pathological inputs freezing the main thread. imgly resizes
// internally, but we cap the source we hand it.
export const MAX_BG_DIM = 2048;

let _imgly = null;
async function imgly() {
  if (!_imgly) _imgly = await import('@imgly/background-removal');
  return _imgly;
}

function modelFor(quality) {
  return (BG_QUALITY[quality] || BG_QUALITY[DEFAULT_BG_QUALITY]).model;
}

// Translate imgly's (key, current, total) progress into a normalized phase +
// percentage the UI can render as a two-stage bar.
function makeProgress(onProgress) {
  if (!onProgress) return undefined;
  return (key, current, total) => {
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    const phase = /fetch|download/i.test(key) ? 'download' : 'inference';
    onProgress({ phase, pct, key });
  };
}

/**
 * Preload the model (and WASM) for a quality tier so the first click is
 * instant. Call when the editor opens. Never throws — preload is best effort.
 */
export async function preloadModel(quality = DEFAULT_BG_QUALITY) {
  try {
    const lib = await imgly();
    if (typeof lib.preload === 'function') {
      await lib.preload({ model: modelFor(quality), output: { format: 'image/png' } });
    }
  } catch (e) {
    // Non-fatal: the real call will download on demand.
    console.warn('bg-removal preload failed:', e);
  }
}

// Downscale a source blob if it exceeds MAX_BG_DIM (keeps aspect). Returns the
// original blob when already small enough.
async function capBlob(blob) {
  try {
    const bmp = await createImageBitmap(blob);
    const max = Math.max(bmp.width, bmp.height);
    if (max <= MAX_BG_DIM) { bmp.close && bmp.close(); return blob; }
    const scale = MAX_BG_DIM / max;
    const c = document.createElement('canvas');
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close && bmp.close();
    return await new Promise((res) => c.toBlob((b) => res(b || blob), 'image/png'));
  } catch (e) {
    return blob; // createImageBitmap unsupported / failed — pass through
  }
}

/**
 * Remove the background from an image blob. Resolves to a PNG blob with the
 * background made transparent. Rejects on failure (caller shows a toast).
 */
export async function removeImageBackground({ blob, quality = DEFAULT_BG_QUALITY, onProgress }) {
  const lib = await imgly();
  const capped = await capBlob(blob);
  return lib.removeBackground(capped, {
    output: { format: 'image/png' },
    model: modelFor(quality),
    progress: makeProgress(onProgress),
  });
}

// ===========================================================================
// Mask-refinement seam (1.7.0) — primitives for the refine-brush agent.
// ===========================================================================

/**
 * Return an editable foreground mask (grayscale PNG blob: white = foreground)
 * for the given source. The refine brush edits THIS, then calls
 * applyForegroundMask() to composite.
 */
export async function segmentForegroundMask({ blob, quality = DEFAULT_BG_QUALITY, onProgress }) {
  const lib = await imgly();
  if (typeof lib.segmentForeground !== 'function') {
    throw new Error('segmentForeground indisponibil in versiunea instalata');
  }
  const capped = await capBlob(blob);
  return lib.segmentForeground(capped, {
    output: { format: 'image/png' },
    model: modelFor(quality),
    progress: makeProgress(onProgress),
  });
}

/**
 * Composite a (possibly hand-edited) mask onto the source, producing the final
 * transparent-background PNG blob.
 */
export async function applyForegroundMask({ blob, mask }) {
  const lib = await imgly();
  if (typeof lib.applySegmentationMask !== 'function') {
    throw new Error('applySegmentationMask indisponibil in versiunea instalata');
  }
  return lib.applySegmentationMask(blob, mask, { output: { format: 'image/png' } });
}
